import { Injectable } from "@angular/core";
import { Platform } from "@ionic/angular";
import { BiometryType, NativeBiometric } from "capacitor-native-biometric";
import { Preferences } from "@capacitor/preferences";
import { EventsService } from "../event/event.service";
import Cookies from "js-cookie";
import { AuthenticationService } from "../authentication/authentication.service";
import { LoginService } from "../login/login.service";
import { UniqueIDService } from "../unique-id/unique-id.service";
import { ConfigService } from "../config/config.service";
import { UserSessionService } from "../user-session/user-session.service";
import { AlertController } from "@ionic/angular";
import { GlobalErrorHandlerService } from "../global-errors/global-error-handler.service";
import { switchMap } from "rxjs/operators";
import { Navigator } from "src/app/components/menu/navigator";

@Injectable({
  providedIn: "root",
})
export class BiometricService {
  public isBiometricAvailable: boolean = false;
  public biometricType: string = "Touch/Face ID";
  public credentials: any = null;
  public credentialExists: boolean = false;
  public verified: boolean = false;
  public useBiometric: boolean = false;
  public verificationError: any = null;
  public error: any = null;
  public loginData = null;
  constructor(
    public platform: Platform,
    public events: EventsService,
    public authenticationService: AuthenticationService,
    public loginService: LoginService,
    private uniqueID: UniqueIDService,
    private configService: ConfigService,
    public navigator: Navigator,
    private alertController: AlertController,
    private userSessionService: UserSessionService,
    private globalErrorHandlerService: GlobalErrorHandlerService
  ) {
    this.loginData = {};

  }

  async biometricLoginInitiator() {
    
      if (!this.verified) {
        await this.isAvailable();
        await this.verifyUser();
        if (this.verified) {
          this.doLogin();
          return true;
        }
      }

  }


  async isAvailable() {
    if (!this.platform.is("capacitor")) return;

    const result = await NativeBiometric.isAvailable();

    await this.getBiometricStatus();

    if (!result.isAvailable) this.isBiometricAvailable = false;
    else {
      await this.getCredentials();
      this.isBiometricAvailable = true;
      this.setBiometryType(result);
    }
  }

  async verifyUser() {
    if (
      this.isBiometricAvailable &&
      this.credentialExists &&
      this.useBiometric
    ) {
      try {
        await NativeBiometric.verifyIdentity({
          reason: "For easy log in to Stardust",
          title: "Log in",
        });
      } catch (e) {
        console.log("Biometric Verification Failed", e);
        this.verificationError = e;
        return;
      }

      this.verified = true;
      console.log("Biometric Verified Successfully");
    }
  }

  async getCredentials() {
    await NativeBiometric.getCredentials({
      server: "www.stardust.com",
    })
      .then((credentials) => {
        console.log("Credentials Found");
        this.credentials = credentials;
        this.credentialExists = true;
      })
      .catch((error) => {
        console.log("Biometric Credentials Not Found");
      });
  }

  async onToggleUseBiometric() {
    this.setBiometricStatus(!this.useBiometric);

    await this.verifyUser();

    if (!this.useBiometric && this.credentialExists) this.deleteCredentials();
  }

  public setCredentials = (credentials) => {
    this.deleteCredentials();

    NativeBiometric.setCredentials({
      username: credentials.username,
      password: credentials.password,
      server: "www.stardust.com",
    }).then(() => console.log("Credentials Added"));
  };

  public deleteCredentials = () => {
    if (!!this.credentials) this.credentials = null;

    this.credentialExists = false;

    NativeBiometric.deleteCredentials({
      server: "www.stardust.com",
    })
      .then(() => console.log("Credentials Deleted"))
      .catch(() => {
        console.log("No Credentials Found To Delete");
      });
  };

  async getBiometricStatus() {
    const biometricStatus = await Preferences.get({ key: "useBiometric" });

    if (biometricStatus.value)
      this.useBiometric = biometricStatus.value === "true" ? true : false;

    if (!biometricStatus.value) this.setBiometricStatus(false);
  }

  async setBiometricStatus(value) {
    this.useBiometric = value;

    await Preferences.set({
      key: "useBiometric",
      value: `${value}`,
    });
  }

  public setBiometryType(result) {
    //multiple biometrics types for android to be added
    if (result.biometryType == BiometryType.FACE_ID) {
      this.biometricType = "Face ID";
    }
    if (result.biometryType == BiometryType.TOUCH_ID) {
      this.biometricType = "Touch ID";
    }
  }

  doLogin() {
    this.authenticationService
      .getCsrfToken()
      .pipe(
        switchMap((response) => {
          if (!!response.payload && !!response.payload.token) {
            this.loginData.csrf = response.payload.token;
            this.loginData.username = this.credentials.username;
            this.loginData.password = this.credentials.password;
            this.loginData.clientapp = this.configService.getClientApp();
            this.loginData.device_id = this.uniqueID.getMFADeviceId();
            let fbp = Cookies.get("_fbp");
            let fbc = Cookies.get("_fbc");
            if (typeof fbp !== "undefined") {
              this.loginData.deviceDetails = fbp;
            }
            if (typeof fbc !== "undefined") {
              this.loginData.externalSource = fbc;
            }
          }
          return this.loginService.loginCustomer(this.loginData);
        })
      )
      .subscribe(
        (response: any) => {
          this.userSessionService.setSessionToken(response.sessionToken);
          this.loginService.setLoginResponseInStorage(true);
        },
        (error) => {
          this.doLoginFailProtocol(error.getErrorCode().toUpperCase());
        }
      );
  }

  doLoginFailProtocol(error: any) {
    let errorCode =
      error && typeof error.getErrorCode === "function"
        ? error.getErrorCode().toUpperCase()
        : "UNKNOWN_ERROR";
    if (!this.error || !(this.error instanceof GlobalErrorHandlerService)) {
      this.error = this.globalErrorHandlerService;
    }

    let errorMessage = this.getErrorMessage(errorCode);

    this.presentAlert(errorMessage);

    this.events.publish("event:login:error", {
      errorCode: this.error.getErrorCode().toUpperCase(),
      errorName: this.error.getErrorMessage(),
    });
    this.navigator.navigateTo('LoginComponent',"",{biometricVerificationFailed:true});
  }
  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case "LOGIN_WRONG_USERNAME":
        return "The provided username is incorrect. Please try again.";
      case "LOGIN_WRONG_PASSWORD":
        return "The provided password is incorrect. Please try again.";
      case "FORCE_PASSWORD_RESET":
        return "An e-mail has been sent to you. Please follow the instructions to reset your password.";
      case "FAILED_TOO_MANY_ATTEMPTS":
        return "Too many attempts, please contact support";
      case "LOGIN_MAX_LOGINS_REACHED":
        return "Your account was disabled upon too many failed logins, please contact support.";
      case "TIME_LIMIT_EXCEEDED":
        return "Time limit exceeded.";
      case "NOT_AUTHORIZED_CLIENT_APP":
        return "You are logging from an unauthorized client app version.";
      case "CUSTOMER_EXCLUDED_BY_AUTHORITY":
        return (
          "Your account has been excluded from this site. Please contact customer service for further information."
        );
      case "CUSTOMER_WRONG_STATUS":
        return "Your account is not in 'Active' status. Please contact support.";
      case "CUSTOMER_SELF_EXCLUDED":
        return "You have self-excluded from this site.";
      case "CUSTOMER_SELF_EXCLUDED_COOL_OFF":
        return "You have suspended yourself from play.";
      case "CUSTOMER_SUSPENDED":
        return "Your account has been temporarily suspended. If this is not resolved in 24 hours please contact support.";
      default:
        return "An error has occurred logging you in. Please try again.";
    }
  }

  async presentAlert(errorMessage: string) {
    const alert = await this.alertController.create({
      header: "Error",
      message: errorMessage,
      buttons: ["OK"],
    });

    await alert.present();
  }
}
